import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { Contact } from '@/types/contacts';

interface ContactFormProps {
  contact?: Contact;
  onSubmit: (data: {
    name: string;
    type: 'email' | 'phone' | 'telegram';
    email?: string | null;
    phone_number?: string | null;
    telegram_chat_id?: string | null;
  }) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export function ContactForm({ contact, onSubmit, onCancel, isSubmitting }: ContactFormProps) {
  const [name, setName] = useState(contact?.name || '');
  const [type, setType] = useState<'email' | 'phone' | 'telegram'>(contact?.type || 'email');
  const [email, setEmail] = useState(contact?.email || '');
  const [phoneNumber, setPhoneNumber] = useState(contact?.phone_number || '');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (type === 'email' && !email.trim()) {
      setError('Email is required');
      return;
    }

    if (type === 'phone' && !phoneNumber.trim()) {
      setError('Phone number is required');
      return;
    }

    try {
      await onSubmit({
        name,
        type,
        email: type === 'email' ? email : null,
        phone_number: type === 'phone' ? phoneNumber : null,
        telegram_chat_id: null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="name">Contact Name</Label>
        <Input
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Operations Team"
          disabled={isSubmitting}
          required
        />
      </div>

      <div>
        <Label htmlFor="type">Type</Label>
        <Select
          value={type}
          onValueChange={(value: 'email' | 'phone' | 'telegram') => setType(value)}
          disabled={!!contact || isSubmitting}
        >
          <SelectTrigger id="type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="phone">Phone (SMS)</SelectItem>
            <SelectItem value="telegram">Telegram</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {type === 'email' && (
        <div>
          <Label htmlFor="email">Email Address</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="ops@example.com"
            disabled={isSubmitting}
            required
          />
        </div>
      )}

      {type === 'phone' && (
        <div>
          <Label htmlFor="phone">Phone Number</Label>
          <Input
            id="phone"
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="+1234567890"
            disabled={isSubmitting}
            required
          />
          <p className="mt-1 text-xs text-gray-500">
            SMS delivery is unavailable. You can save this phone number for future use.
          </p>
        </div>
      )}

      {type === 'telegram' && (
        <div className="text-xs text-gray-500">
          Telegram contacts are linked after creation. You'll connect via the bot on the contact
          details page.
        </div>
      )}

      {error && <div className="text-sm text-red-600">{error}</div>}

      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : contact ? 'Update' : 'Create'}
        </Button>
      </div>
    </form>
  );
}
